import type { ElementType } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface EditorialFeatureCardProps {
  eyebrow?: string;
  title: string;
  description: string;
  icon?: LucideIcon;
  href?: string;
  ctaLabel?: string;
  variant?: "light" | "accent" | "minimal";
  index?: string;
}

const EditorialFeatureCard = ({
  eyebrow,
  title,
  description,
  icon: Icon,
  href,
  ctaLabel = "Learn More",
  variant = "light",
  index,
}: EditorialFeatureCardProps) => {
  const Wrapper = (href ? Link : "article") as ElementType;
  const wrapperProps = href ? { to: href } : {};
  const isLight = variant === "light";
  const isAccent = variant === "accent";
  const isMinimal = variant === "minimal";

  return (
    <Wrapper
      {...(wrapperProps as object)}
      className={cn(
        "group block transition-colors duration-300",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2",
        isLight &&
          "border-l border-border/80 pl-6 md:pl-8 pr-2 py-6 md:py-8 hover:border-accent/45 focus-visible:ring-offset-background",
        isAccent &&
          "bg-secondary/96 border border-accent/16 p-6 md:p-8 lg:p-10 hover:border-accent/30 focus-visible:ring-offset-secondary",
        isMinimal &&
          "py-5 md:py-7 pr-2 border-t border-border/70 hover:border-accent/40 focus-visible:ring-offset-background"
      )}
    >
      <div
        className={cn(
          "flex items-start justify-between gap-6",
          isMinimal ? "mb-8 md:mb-9" : "mb-10 md:mb-11"
        )}
      >
        <div className="space-y-4 md:space-y-5">
          {eyebrow && (
            <p className="font-body text-[10px] uppercase tracking-[0.24em] text-accent">
              {eyebrow}
            </p>
          )}
          {index && (
            <p
              className={cn(
                "font-headline leading-none",
                isMinimal ? "text-4xl md:text-5xl" : "text-5xl md:text-6xl",
                isLight && "text-accent/24",
                isAccent && "text-accent/34",
                isMinimal && "text-accent/22"
              )}
            >
              {index}
            </p>
          )}
        </div>

        {Icon && (
          <div
            className={cn(
              "flex shrink-0 items-center justify-center text-accent",
              isMinimal ? "h-9 w-9" : "h-10 w-10 md:h-11 md:w-11",
              isAccent ? "border-b border-accent/22" : "border-b border-accent/16"
            )}
          >
            <Icon
              className={cn(isMinimal ? "w-4 h-4" : "w-4.5 h-4.5 md:w-5 md:h-5")}
              strokeWidth={1.55}
              aria-hidden="true"
            />
          </div>
        )}
      </div>

      <h3
        className={cn(
          "font-headline leading-[1.08]",
          isMinimal ? "text-[1.9rem] md:text-[2.2rem] mb-4 md:mb-5" : "text-[2rem] md:text-[2.35rem] mb-5 md:mb-6",
          isLight || isMinimal ? "text-foreground" : "text-secondary-foreground"
        )}
      >
        {title}
      </h3>

      <p
        className={cn(
          "max-w-[32rem] text-base md:text-[1.02rem] leading-[1.85]",
          isLight || isMinimal ? "text-muted-foreground" : "text-secondary-foreground/78"
        )}
      >
        {description}
      </p>

      {href && (
        <div className={cn(isMinimal ? "pt-8" : "pt-9")}>
          <span className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.14em] text-accent transition-colors group-hover:text-accent/85">
            {ctaLabel}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      )}
    </Wrapper>
  );
};

export default EditorialFeatureCard;
